import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, HandHeart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function AdminSocialWork() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", description: "", location: "", duration_hours: 40, is_active: true };

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/social-work");
    setItems(r.data || []);
  };

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/social-work/${editing.id}`, editing);
      } else {
        await api.post("/admin/social-work", editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this programme?")) return;
    await api.delete(`/admin/social-work/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl font-bold flex items-center gap-2"><HandHeart className="text-amber-500" size={24} /> Social Work Programmes</h1>
        <button onClick={() => setEditing(empty)} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-1" data-testid="new-socwork-button">
          <Plus size={16} /> New Programme
        </button>
      </div>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="p-10 rounded-xl border border-dashed border-slate-300 text-center col-span-full">
            <HandHeart size={36} className="mx-auto text-slate-300" />
            <div className="mt-3 text-slate-500">No social-work programmes yet.</div>
          </div>
        )}
        {items.map((i) => (
          <div key={i.id} className="p-5 rounded-xl border border-slate-200 bg-white" data-testid={`admin-socwork-${i.id}`}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-bold">{i.duration_hours || 40} HOURS</div>
            <h3 className="heading text-lg font-bold mt-1">{i.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-3">{i.description}</p>
            {i.location && <div className="mt-2 text-xs text-slate-500">📍 {i.location}</div>}
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className={`px-2 py-1 rounded font-bold ${i.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {i.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(i)} className="text-slate-700 hover:bg-slate-100 p-2 rounded" data-testid={`edit-socwork-${i.id}`}><Edit size={14} /></button>
              <button onClick={() => del(i.id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Create"} Social Work Programme</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Inp label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} test="socwork-title-input" />
              <Area label="Description" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} test="socwork-desc-input" />
              <Inp label="Location" v={editing.location || ""} on={(v) => setEditing({ ...editing, location: v })} test="socwork-loc-input" />
              <Inp label="Duration (hours)" v={editing.duration_hours || ""} on={(v) => setEditing({ ...editing, duration_hours: Number(v) || 0 })} test="socwork-hours-input" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-amber-500" />
                Active (visible publicly)
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Cancel</button>
            <button onClick={save} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg px-5 py-2 text-sm" data-testid="socwork-save-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Inp({ label, v, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none" data-testid={test} />
    </div>
  );
}
function Area({ label, v, on, test }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
      <textarea rows={4} value={v} onChange={(e) => on(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none resize-none" data-testid={test} />
    </div>
  );
}
